"use client"

import toast from "react-hot-toast"
import { Progress } from "visuais"
import { CheckCircle, X } from "lucide-react"

interface ProgressToastOptions {
  title: string
  steps: string[]
  successMessage: string
}

export function useProgressToast() {
  const showProgressToast = ({ title, steps, successMessage }: ProgressToastOptions) => {
    let currentStep = 0
    const totalSteps = steps.length

    const toastId = toast(
      (t) => (
        <div className="space-y-2">
          <div className="font-medium">{title}</div>
          <div className="text-sm text-muted-foreground">
            {steps[currentStep]} ({currentStep + 1}/{totalSteps})
          </div>
          <Progress value={(currentStep / totalSteps) * 100} className="w-full" />
        </div>
      ),
      {
        duration: Number.POSITIVE_INFINITY,
      },
    )

    // Função para atualizar o progresso
    const updateProgress = (stepIndex: number) => {
      currentStep = stepIndex
      const progress = ((currentStep + 1) / totalSteps) * 100

      if (currentStep < totalSteps - 1) {
        toast(
          (t) => (
            <div className="space-y-2">
              <div className="font-medium">{title}</div>
              <div className="text-sm text-muted-foreground">
                {steps[currentStep + 1]} ({currentStep + 2}/{totalSteps})
              </div>
              <Progress value={progress} className="w-full" />
            </div>
          ),
          {
            id: toastId,
            duration: Number.POSITIVE_INFINITY,
          },
        )
      } else {
        toast(
          (t) => (
            <div className="space-y-2">
              <div className="flex items-center gap-2 font-medium text-green-800 dark:text-green-500">
                <CheckCircle className="h-4 w-4" />
                Concluído!
              </div>
              <div className="text-sm">{successMessage}</div>
              <Progress value={100} className="w-full" />
            </div>
          ),
          {
            id: toastId,
            duration: 3000,
          },
        )
      }
    }

    // Função para marcar como erro
    const markAsError = (errorMessage: string) => {
      toast(
        (t) => (
          <div className="flex items-center gap-2">
            <X className="h-4 w-4 text-red-500" />
            <div>
              <div className="font-medium text-red-600">Erro</div>
              <div className="text-sm text-red-600">{errorMessage}</div>
            </div>
          </div>
        ),
        {
          id: toastId,
          duration: 5000,
        },
      )
    }

    return {
      updateProgress,
      markAsError,
      dismiss: () => toast.dismiss(toastId),
    }
  }

  return { showProgressToast }
}
